import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Link, useLocation } from "react-router-dom";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  bodyOpacityTransitionAtom,
  clickHideAtom,
  currentNavAtom,
  localCustomNavAtom,
  userPermissionAtom,
  logInfoAtom,
} from "../../store";

// import { driver } from "driver.js";
import { checkMobile } from "../../utils/format";
// import { useTranslation } from "react-i18next";
import { PermissionContext } from "../../utils/context";
import styles from "./nav.module.css";
import DeleteNavIcon from "./delete.svg";
import * as Sentry from "@sentry/react";
import { useCustomNav } from "@/utils/customhooks/useCustom";

type Navs = {
  title: string;
  location: string;
  permission: string;
  hoverText?: string;
  key?: string;
}[];

function getActualWidthOfChars(
  text: string,
  options: { size?: number; family?: string } = {}
) {
  const { size = 14, family = "Helvetica Neue" } = options;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = `500 ${size}px ${family}`;
  const metrics = ctx.measureText(text);
  const actual =
    Math.abs(metrics.actualBoundingBoxLeft) +
    Math.abs(metrics.actualBoundingBoxRight);
  return Math.max(metrics.width, actual);
}

function Nav() {
  const [currentNav, setCurrentNav] = useRecoilState(currentNavAtom);
  // const [opacity, setOpacity] = useState(1);
  const userPermission = useRecoilValue(userPermissionAtom);
  const bodyOpacityTransition = useRecoilValue(bodyOpacityTransitionAtom);
  const [logInfo] = useRecoilState<{
    preferences: {
      customNav: string;
    };
  }>(logInfoAtom);

  const clickHide = useRecoilValue(clickHideAtom);
  const location = useLocation();
  const [hoverIndex, setHoverIndex] = useState(-1);
  const navRef = useRef<HTMLDivElement>(null);
  const { isAllowedTo } = useContext<{
    isAllowedTo: (permission: string) => boolean;
  }>(PermissionContext);
  const timer = useRef(null);
  // const { t } = useTranslation();
  const isMobile = checkMobile();
  const [isVIP] = useState(true);

  const permissionPathMap = {
    chatavatar_text_access: "/chatavatar",
    panorama_access: "/panorama",
    rodin_access: "/rodin",
    omnicraft_access: "/omnicraft",
  };

  const editList = [
    {
      title: "HDRI Generation",
      location: "/omnicraft/hdri",
      permission: "omnicraft.view",
      key: "HDR_Generation",
    },
    {
      title: "Texture Generator",
      location: "/omnicraft/texture",
      permission: "omnicraft.view",
      key: "Texture_Generator",
    },
    {
      title: "Rodin Search",
      location: "/omnicraft/search",
      permission: "omnicraft.view",
      key: "Rodin_Search",
    },
    {
      title: "Mesh Editor",
      location: "/omnicraft/mesh",
      permission: "omnicraft.view",
      key: "Mesh_Editor",
    },
    {
      title: "Model Viewer",
      location: "/omnicraft/viewer",
      permission: "omnicraft.view",
      key: "Model_Viewer",
    },
    {
      title: "Format Convertor",
      location: "/omnicraft/formatcon",
      permission: "omnicraft.view",
      key: "Format_Convertor",
    },

    // {
    //   title: "Microstructure Generator",
    //   location: "/microstructure-generator",
    //   permission: "",
    // },
    // {
    //   title: "Material Generator",
    //   location: "/material-generator",
    //   permission: "",
    // },
  ] as Navs;

  const ominicraftNavItems = useMemo(
    () =>
      (isVIP && !isMobile
        ? [
            {
              title: "Texture Generator",
              hoverText: "Texture Generator",
              location: "/omnicraft/texture",
              permission: "omnicraft.view",
            },
          ]
        : []
      ).filter((item, _) => {
        return isAllowedTo(item.permission);
      }),
    [isVIP, isAllowedTo]
  );
  const navItems = useMemo(
    () =>
      [
        // {
        //   title: "ChatAvatar",
        //   hoverText: "Text/Image to 3D Avatar",
        //   location: "/chatavatar",
        //   permission: "chatavatar.view",
        // },
        {
          title: "Rodin",
          hoverText: "3D Large Model",
          location: "/",
          permission: "rodin.view",
        },
      ].filter((item, _) => {
        return isAllowedTo(item.permission);
      }),
    [isAllowedTo]
  );

  const setlocalCustomNav = useSetRecoilState<
    {
      title: string;
      location: string;
      permission: string;
      hoverText?: string;
      key?: string;
    }[]
  >(localCustomNavAtom);

  const { customNav, setCustomNav } = useCustomNav();

  useEffect(() => {
    const fn = async () => {
      if (logInfo && logInfo?.preferences?.customNav) {
        setlocalCustomNav(() => {
          try {
            return JSON.parse(logInfo.preferences.customNav)
              .map((storgeNav) => {
                return editList.find((edit) => edit.key === storgeNav);
              })
              .filter((i) => i);
          } catch (e) {
            Sentry.captureException(e);
            return [];
          }
        });
      }
    };
    fn();
  }, [logInfo]);

  const showNav = [...navItems, ...ominicraftNavItems, ...customNav];

  const pathMenu = ["chatavatar", "panorama", "rodin", "omnicraft"].filter(
    (_, index) => {
      const accessKey = Object.keys(permissionPathMap)[index];
      return userPermission[accessKey];
    }
  );

  useEffect(() => {
    const title = showNav.find(
      (nav) => location.pathname === nav.location
    )?.title;
    if (title) {
      setCurrentNav(title);
    } else if (
      location.pathname.includes("/chatavatar") ||
      location.pathname.includes("/imageto3d")
    ) {
      setCurrentNav("ChatAvatar");
    } else if (location.pathname.includes("/panorama")) {
      setCurrentNav("Panorama");
    } else if (location.pathname.includes("/rodin")) {
      setCurrentNav("Rodin");
    } else if (location.pathname.includes("/omnicraft")) {
      setCurrentNav("OmniCraft");
    }
    // if (!localStorage.getItem('switchtoRodin') && !checkMobile()) {
    //     setTimeout(() => {
    //         driverGuide()
    //         localStorage.setItem('switchtoRodin', true)
    //     }, 500);
    // }
  }, [location.pathname, showNav.length]);

  useEffect(() => {
    if (bodyOpacityTransition) {
      navRef.current.style.transform = "translateX(-300px)";
    } else {
      navRef.current.style.transform = "translateX(0)";
    }
  }, [bodyOpacityTransition]);

  const handleClick = async (index) => {
    if (isMobile) {
      window.location.href = showNav[index].location;
    } else {
      console.log('location',location.pathname)
      clearTimeout(timer.current);
      setHoverIndex(-1);
      // setCurrentNav(showNav[index].title);
      if (!localStorage.getItem("switchtoRodin")) {
        localStorage.setItem("switchtoRodin", "true");
      }
    }
  };

  const handleMouseMove = (index) => {
    if (!editMode && index === 2) {
      setEditMode(true);
    }
    if (currentNav === showNav[index].title) return;
    setHoverIndex(index);
  };

  const handleMouseLeave = () => {
    if (!editMode) {
      clearTimeout(timer.current);
      setHoverIndex(-1);
    }
  };

  const fontWidth = useMemo(() => {
    const widthMap = new Map<string, number>();
    [...navItems, ...editList, ...ominicraftNavItems].forEach(
      (item: { title: string; hoverText?: string }) => {
        widthMap.set(item.title, getActualWidthOfChars(item.title));
        if (item.hoverText) {
          widthMap.set(item.hoverText, getActualWidthOfChars(item.hoverText));
        }
      }
    );
    return widthMap;
  }, [navItems, ominicraftNavItems]);

  const navLinks = useRef<{ [key: string]: HTMLLIElement }>({});
  const [editMode, setEditMode] = useState(false);
  const editNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editMode && editNavRef.current) {
      const handleMove = (e: MouseEvent) => {
        if (!editNavRef.current || !navRef.current) return;
        const rect = editNavRef.current.getBoundingClientRect();
        const inWrapper =
          e.x > rect.left &&
          e.x < rect.right &&
          e.y > rect.top - navRef.current.offsetHeight - 8 &&
          e.y < rect.bottom;
        if (!inWrapper) {
          setHoverIndex(-1);
          setEditMode(false);
        }
      };
      window.addEventListener("pointermove", handleMove, {
        capture: true,
      });
      return () => {
        window.removeEventListener("pointermove", handleMove, {
          capture: true,
        });
      };
    }
  }, [editMode]);

  return (
    <>
      {!isMobile ? (
        <div
          id="navElement"
          className={`fixed flex-center z-[999] left-0 right-0 m-auto w-max ${
            clickHide ? "opacity-0 invisible" : ""
          } ${editMode ? styles["will-add"] : ""}`}
          style={{
            top: "30px",
          }}
        >
          <nav
            ref={navRef}
            id="imgBtnElement"
            className={`flex justify-around w-full bg-[rgba(255,255,255,0.1)] rounded-full p-2 transition-300-ease flex-col gap-[6px] flex-[0] flex-center before:absolute-full before:backdrop-blur-[40px] before:rounded-full ${styles["edit-nav-padding"]}`}
          >
            <ul className="w-full flex items-center justify-center gap-2">
              <>
                {showNav.map((item, index) => (
                  <div
                    className="group m-0 list-none p-0 relative flex items-center gap-2"
                    key={item.title}
                    onClick={handleClick.bind(null, index)}
                  >
                    <div className="h-5 w-px bg-gradient-to-b from-transparent via-[#ffffff26] to-transparent group-first:hidden"></div>
                    <div
                      className="group m-0 list-none items-center gap-2 p-0 relative"
                      ref={(ref) => {
                        navLinks.current[item.title] =
                          ref as unknown as HTMLLIElement;
                      }}
                      style={{
                        viewTransitionName: item.title.replace(" ", ""),
                      }}
                    >
                      <Link
                        to={{
                          pathname: item.location,
                          search: location.search,
                        }}
                        onMouseEnter={() => {
                          handleMouseMove(index);
                        }}
                        onMouseLeave={handleMouseLeave}
                        className={`text-nowrap flex-center select-none rounded-full px-4 py-[7px] text-sm transition-all duration-300 ease-out text-white max-w-[500px] w-fit	 ${
                          currentNav === item.title
                            ? "bg-[rgba(255,255,255,0.1)] backdrop-blur-2xl"
                            : `opacity-60`
                        } ${
                          hoverIndex === index
                            ? "bg-[rgba(255,255,255,0.1)] max-w-[1000px] !opacity-100"
                            : ""
                        } leading-[18px]`}
                        style={{
                          width:
                            (hoverIndex === index
                              ? fontWidth.get(item.hoverText || item.title)
                              : fontWidth.get(item.title)) + 34,
                        }}
                      >
                        {item?.hoverText ? (
                          <>
                            <p
                              className="overflow-clip h-[18px] transition-[opacity] duration-300"
                              style={{
                                opacity: hoverIndex === index ? 0 : 1,
                                width: hoverIndex === index ? 0 : "fit-content",
                              }}
                            >
                              {item.title}
                            </p>
                            <p
                              className="overflow-clip h-[18px] transition-[opacity] duration-300"
                              style={{
                                opacity: hoverIndex === index ? 1 : 0,
                                width: hoverIndex === index ? "fit-content" : 0,
                              }}
                            >
                              {item?.hoverText}
                            </p>
                          </>
                        ) : (
                          item.title
                        )}
                      </Link>
                    </div>
                    {item.title === "OmniCraft" && (
                      <div
                        className={`${styles.show} opacity-0 pointer-events-none transition-opacity flex flex-center absolute flex-col left-1/2 top-full -translate-x-1/2 w-[248px] translate-y-4 bg-[rgba(136,136,136,0.2)] py-3 rounded-2xl backdrop-blur-[150px]`}
                        ref={editNavRef}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {editList.map((editItem) => (
                          <div
                            className={`h-[34px] py-2 pl-6 pr-2 w-full flex justify-between items-center cursor-pointer flex-shrink-0 text-nowrap transition-all duration-300 text-sm group/item ${
                              customNav.some(
                                (nav) => nav.title === editItem.title
                              )
                                ? `text-transparent bg-clip-text bg-[linear-gradient(180deg,#28E4F0_50%,#28B4F0_100%)] font-bold ${styles.shinning}`
                                : "opacity-50 text-white hover:opacity-80"
                            }`}
                            onClick={() => {
                              window.open(editItem.location, "_self");
                            }}
                            key={editItem.title}
                          >
                            {editItem.title}
                            {customNav.every(
                              (nav) => nav.title !== editItem.title
                            ) ? (
                              logInfo && (
                                <div
                                  className={`${styles.addWrapper} flex-center rounded-full w-[30px] h-[20px] flex hover:bg-[rgba(255,255,255,0.1)] transition-all duration-300 opacity-0 group-hover/item:opacity-100`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      customNav.every(
                                        (nav) => nav.title !== editItem.title
                                      )
                                    ) {
                                      if (document.startViewTransition) {
                                        document.startViewTransition(() => {
                                          setCustomNav((prev) => [
                                            ...prev,
                                            editItem,
                                          ]);
                                        });
                                      } else {
                                        setCustomNav((prev) => [
                                          ...prev,
                                          editItem,
                                        ]);
                                      }
                                    }
                                  }}
                                >
                                  <div className={styles.addButton} />
                                </div>
                              )
                            ) : (
                              <img
                                src={DeleteNavIcon}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (document.startViewTransition) {
                                    const transitions =
                                      document.startViewTransition(() => {
                                        navLinks.current[
                                          editItem.title
                                        ].classList.add("hidden");
                                        `text-transparent bg-clip-text bg-[linear-gradient(180deg,#28E4F0_50%,#28B4F0_100%)] font-bold ${styles.shinning} opacity-80 text-white`
                                          .split(" ")
                                          .forEach((className) => {
                                            (
                                              e.target as HTMLElement
                                            ).parentElement?.classList?.toggle(
                                              className
                                            );
                                          });
                                      });
                                    transitions.finished.then(() => {
                                      `opacity-80 text-white`
                                        .split(" ")
                                        .forEach((className) => {
                                          (
                                            e.target as HTMLElement
                                          ).parentElement?.classList?.toggle(
                                            className
                                          );
                                        });
                                      setCustomNav((prev) => [
                                        ...prev.filter(
                                          (p) => p.title !== editItem.title
                                        ),
                                      ]);
                                      navLinks.current[
                                        editItem.title
                                      ].classList.remove("hidden");
                                    });
                                  } else {
                                    setCustomNav((prev) =>
                                      prev.filter(
                                        (p) => p.title !== editItem.title
                                      )
                                    );
                                  }
                                }}
                                className=" group-hover/item:opacity-80 opacity-0 group-hover/item:hover:opacity-100 transition-all duration-300 mr-[5px] flex flex-center"
                                width={20}
                                height={20}
                                alt="toolkit"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            </ul>
          </nav>
        </div>
      ) : (
        <div
          className={`absolute flex-center top-[90px] z-[999] left-[0%] ${
            clickHide ? "opacity-0 invisible" : ""
          }`}
          style={{
            top: "16px",
          }}
        >
          <nav
            ref={navRef}
            id="imgBtnElement"
            className={`flex justify-around w-full rounded-full p-2 backdrop-blur-[40px] transition-300-ease`}
          >
            <ul className="w-full flex items-center justify-center gap-2">
              <>
                {navItems.map((item, index) => (
                  <li
                    className="group m-0 flex list-none items-center gap-2 p-0"
                    key={index}
                  >
                    <Link
                      to={`/${pathMenu[index]}`}
                      onClick={handleClick.bind(null, index)}
                      onMouseEnter={handleMouseMove.bind(null, index)}
                      onMouseLeave={handleMouseLeave}
                      className={`flex-center h-6 select-none rounded-full px-2 text-sm font-medium transition-all duration-300 ease-out hover:bg-[rgba(255,255,255,0.1)] ${
                        currentNav === item.title
                          ? "text-white bg-[rgba(255,255,255,0.1)] backdrop-blur-2xl"
                          : hoverIndex === 2
                          ? "text-[#ffffff50] bg-[rgba(255,255,255,0.1)]"
                          : "text-[#ffffff50] hover:animation-nav-fade"
                      }`}
                    >
                      {/* {item.title} */}
                      {hoverIndex === index ? item.hoverText : item.title}
                    </Link>
                    <div className="h-5 w-px bg-gradient-to-b from-transparent via-[#ffffff26] to-transparent group-last:hidden"></div>
                  </li>
                ))}
              </>
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}

export default Nav;
